// url-safety — as partes PURAS do guard de SSRF, compartilhadas entre backend e
// cliente. Mesmo padrão (e mesmo motivo) do `tmdb.ts`: quando os dois lados
// precisam da MESMA decisão, a função mora aqui e é importada pelos dois — senão
// eles divergem e a divergência é justamente a brecha.
//
// POR QUE O CLIENTE PRECISA DISTO
// -------------------------------
// Até a decisão "o proxy é WEB-only; na TV a imagem vai DIRETO"
// (lib/image-source.ts), TODA capa passava pelo `/v1/proxy/img`, e o backend
// aplicava `validateUrl` antes de tocar na origem. Indo direto, o `<img>` da TV
// é quem faz o request — e o guard do servidor deixou de estar no caminho.
// Um provedor hostil (a URL da capa vem do painel Xtream DELE) pode apontar a
// capa pra `http://192.168.0.1/...` e fazer a TV do usuário bater na LAN da casa
// dele. A resposta é opaca num `<img>` (não há exfiltração de conteúdo), mas o
// par onload/onerror é um ORÁCULO de host/porta: dá pra varrer a rede local.
//
// O QUE MORA AQUI E O QUE NÃO MORA
// --------------------------------
// Aqui: só o que é PURO e roda nos dois lados (scheme, IP literal, hostname de
// LAN). O que depende de DNS (`isHostnameSafe`, `validateUrl`) fica no backend
// (`apps/api/src/services/ssrf-guard.ts`) — não há `node:dns` no cliente, e a
// defesa contra DNS rebind exige revalidar a cada hop de redirect, coisa que só
// quem controla o fetch consegue fazer.
//
// Consequência aceita e explícita: o guard do cliente é ESTRITAMENTE MAIS FRACO
// que o do servidor — ele não pega `evil.com → 192.168.0.1` via DNS. Ele fecha o
// caso trivial e barato (IP literal / host de LAN), que é o vetor real de um
// painel hostil. Não é, e não pretende ser, o guard completo: o do servidor
// continua sendo a fronteira de verdade no caminho WEB.
/**
 * 🔒 Reduz QUALQUER URL a `protocol//host` (sem userinfo, path, query ou hash).
 *
 * Por que mora aqui (compartilhado backend/cliente/observability): a URL `.ts` do
 * Xtream embute as credenciais (user/pass) no PATH —
 * `http://host:porta/USER/PASS/id.ts` — e às vezes na query. Qualquer sink de
 * TEXTO que receba a URL crua (log, `PlayerErrorPayload.message`, breadcrumb do
 * Sentry, telemetria) vaza a credencial do provedor. Esta é a função ÚNICA que
 * corta isso: nenhum segmento de credencial sobrevive a ela, só o host debugável.
 *
 * Usada por: o ramo native-ts do `WebPlayer` (a URL vai pro `video.src`, legível
 * no DOM — ver lá) e o scrub de breadcrumb/evento do Sentry (`observability/
 * sentry.ts`), onde os loaders de fetch/xhr (probe do nativo E o loader do
 * mpegts.js) gravariam a URL crua ANTES de qualquer redação nossa.
 *
 * ⚠️ NÃO é decisão de segurança de rede (isso é `isPubliclyRoutableUrl`) — é
 * puramente redação de credencial pra observabilidade. URL inválida → sentinela.
 */
export function redactStreamUrl(url) {
    try {
        const u = new URL(url);
        return `${u.protocol}//${u.host}`;
    }
    catch {
        return '[redacted-url]';
    }
}
/** Schemes permitidos. Qualquer outro (file:, gopher:, ftp:, javascript:) é
 *  rejeitado. */
export const ALLOWED_SCHEMES = new Set(['http:', 'https:']);
/**
 * Portas explicitamente permitidas — **regra de SERVIDOR, não de cliente**.
 * Aceitar qualquer porta num fetch de servidor abre vetor de port scanning
 * (timing diferencial entre porta aberta e fechada).
 *
 * ATENÇÃO ao reusar isto no cliente: painel Xtream serve capa em porta exótica
 * o tempo todo (8000, 2095, 25461...). No WEB isso já morre no proxy hoje; na TV
 * a capa vai direto e FUNCIONA. Aplicar esta allowlist no caminho direto sumiria
 * com capa legítima de meio catálogo — regressão de produto pra fechar um port
 * scan que o `<img>` já permite contra qualquer host público de qualquer jeito.
 * Por isso `isPubliclyRoutableUrl` NÃO checa porta: ela fecha o alvo (a LAN),
 * não a porta.
 */
export const ALLOWED_PORTS = new Set([80, 443, 8080, 8443]);
/** Hosts onde EXIGIMOS SSL válido (TMDB e variantes). Em outros hosts,
 *  `rejectUnauthorized:false` é tolerado no proxy (CDN Xtream com SSL quebrado é
 *  exatamente o motivo de o proxy existir). */
export function isTrustedHost(host) {
    const h = host.toLowerCase();
    return h === 'image.tmdb.org' || h === 'themoviedb.org' || h.endsWith('.themoviedb.org');
}
/**
 * Canonicaliza um literal de IP como o `URL.hostname` o entrega.
 *
 * ⚠️ FONTE ÚNICA dessa normalização, e não por elegância: `new URL('http://[::1]/')
 * .hostname` devolve `[::1]` — COM colchetes. A metade CLIENTE (`isPrivateHostname`)
 * sempre tirou; a metade SERVIDOR (`isHostnameSafe` → `isPrivateIp`) não tirava, e
 * por isso TODO literal IPv6 passava direto pelo guard de SSRF do backend. Duas
 * cópias da mesma normalização = uma delas vai ficar pra trás; agora é uma só, e
 * quem normaliza é o próprio `isPrivateIp` (o caller não tem como esquecer).
 */
export function canonicalizeIpLiteral(host) {
    return host.trim().toLowerCase().replace(/^\[/, '').replace(/\]$/, '');
}
/** Um octeto IPv4 válido em decimal (0-255), sem zeros à esquerda maliciosos. */
function parseV4Octet(part) {
    if (!/^\d{1,3}$/.test(part))
        return null;
    const n = Number(part);
    return n >= 0 && n <= 255 ? n : null;
}
/**
 * Expande um literal IPv6 nos seus 8 grupos de 16 bits. `null` se NÃO for um
 * IPv6 sintaticamente válido — e o caller trata `null` como INSEGURO (fail-closed).
 *
 * ⚠️ Por que parser e não regex: a classificação por TEXTO (`/^f[cd]../`,
 * `/^::ffff:(.+)$/`) só reconhece a forma que o autor imaginou. O WHATWG
 * re-serializa TODO literal IPv6 na forma comprimida com grupos em HEX — isto é,
 * `http://[::ffff:127.0.0.1]/` chega ao guard como `[::ffff:7f00:1]`, e
 * `[fe80:0:0:0:0:0:0:1]` chega como `[fe80::1]`. Um regex sobre a forma que o
 * ATACANTE digitou nunca vê a forma que o CONSUMIDOR usa. Grupos são a única
 * representação em que as duas formas coincidem.
 */
function parseIpv6Groups(raw) {
    let text = raw;
    // Zone id (`fe80::1%eth0`) é escopo de interface local — não existe destino
    // remoto legítimo com ele. Fail-closed no caller (parse falha → inseguro).
    if (text.includes('%'))
        return null;
    if (!text.includes(':'))
        return null;
    // Sufixo IPv4 embutido (`::ffff:127.0.0.1`, `::127.0.0.1`, `64:ff9b::8.8.8.8`).
    // Reescreve pros DOIS grupos hex equivalentes e segue no parser único — fatiar o
    // texto por índice quebra justo no caso `::1.2.3.4` (o prefixo é `::`, não `:`).
    const embedded = text.match(/^(.*:)((?:\d{1,3}\.){3}\d{1,3})$/);
    if (embedded) {
        const octets = embedded[2].split('.').map(parseV4Octet);
        if (octets.some((n) => n === null))
            return null;
        const [a, b, c, d] = octets;
        const hi = ((a << 8) | b).toString(16);
        const lo = ((c << 8) | d).toString(16);
        text = `${embedded[1]}${hi}:${lo}`;
    }
    else if (text.includes('.')) {
        // Tem ponto mas não é um sufixo IPv4 bem-formado → não é IPv6 válido.
        return null;
    }
    const parts = text.split('::');
    if (parts.length > 2)
        return null; // `::` só pode aparecer UMA vez
    const toGroups = (chunk) => {
        if (chunk === '')
            return [];
        const out = [];
        for (const g of chunk.split(':')) {
            if (!/^[0-9a-f]{1,4}$/.test(g))
                return null;
            out.push(parseInt(g, 16));
        }
        return out;
    };
    if (parts.length === 1) {
        // Sem `::`: precisa dos 8 grupos escritos por extenso.
        const groups = toGroups(parts[0]);
        if (!groups)
            return null;
        return groups.length === 8 ? groups : null;
    }
    const head = toGroups(parts[0]);
    const rest = toGroups(parts[1]);
    if (!head || !rest)
        return null;
    const known = head.length + rest.length;
    // `::` tem que comprimir PELO MENOS um grupo de zeros; `known >= 8` é forma
    // inválida → fail-closed (o caller trata `null` como inseguro).
    if (known >= 8)
        return null;
    const zeros = new Array(8 - known).fill(0);
    return [...head, ...zeros, ...rest];
}
/** Reconstrói o dotted-quad de dois grupos de 16 bits (o IPv4 embutido no v6). */
function v4FromGroups(hi, lo) {
    return `${(hi >> 8) & 0xff}.${hi & 0xff}.${(lo >> 8) & 0xff}.${lo & 0xff}`;
}
/**
 * O IPv6 (já expandido em 8 grupos) aponta pra um destino que o servidor NÃO
 * pode buscar? Classificação por PREFIXO DE BITS — a mesma que uma stack de rede
 * faz — em vez de casamento de texto.
 */
function isPrivateIpv6Groups(g) {
    const [g0, g1, g2, g3, g4, g5, g6, g7] = g;
    // ::ffff:0:0/96 — IPv4-mapped. É ASSIM que `[::ffff:127.0.0.1]` chega aqui
    // (serializado `::ffff:7f00:1`). Delega ao classificador IPv4, que já está
    // correto: mapped de IP público (`::ffff:8.8.8.8`) segue passando.
    if (g0 === 0 && g1 === 0 && g2 === 0 && g3 === 0 && g4 === 0 && g5 === 0xffff) {
        return isPrivateIp(v4FromGroups(g6, g7));
    }
    // ::ffff:0:0:0/96 — IPv4-translated (RFC 2765/SIIT). Mesma delegação.
    if (g0 === 0 && g1 === 0 && g2 === 0 && g3 === 0 && g4 === 0xffff && g5 === 0) {
        return isPrivateIp(v4FromGroups(g6, g7));
    }
    // ::/96 — unspecified (`::`), loopback (`::1`) e IPv4-compatible (`::7f00:1`,
    // que é como `[::127.0.0.1]` se serializa). O IPv4-compatible foi DEPRECADO
    // (RFC 4291 §2.5.5.1) e nada roteável mora aí: bloqueia o /96 inteiro em vez
    // de delegar, senão `::8.8.8.8` viraria um alias "público" de uma forma que
    // stack nenhuma deveria aceitar.
    if (g0 === 0 && g1 === 0 && g2 === 0 && g3 === 0 && g4 === 0 && g5 === 0)
        return true;
    // 64:ff9b::/96 e 64:ff9b:1::/48 — NAT64 well-known (RFC 6052/8215): o tradutor
    // entrega os 32 bits finais como destino IPv4. `64:ff9b::7f00:1` = 127.0.0.1.
    if (g0 === 0x64 && g1 === 0xff9b && g2 === 0 && g3 === 0 && g4 === 0 && g5 === 0) {
        return isPrivateIp(v4FromGroups(g6, g7));
    }
    if (g0 === 0x64 && g1 === 0xff9b && g2 === 0x0001)
        return true;
    // 2002::/16 — 6to4 (RFC 3056): os 32 bits seguintes SÃO o IPv4 do endpoint.
    // `2002:7f00:1::` alcança 127.0.0.1.
    if (g0 === 0x2002)
        return isPrivateIp(v4FromGroups(g1, g2));
    // 2001::/32 — Teredo (RFC 4380): carrega IPv4 do servidor e do cliente
    // (este último ofuscado por XOR). Túnel obsoleto; nenhum CDN legítimo mora aí.
    if (g0 === 0x2001 && g1 === 0x0000)
        return true;
    // 100::/64 — discard-only (RFC 6666). Sink, não destino.
    if (g0 === 0x0100 && g1 === 0 && g2 === 0 && g3 === 0)
        return true;
    // fc00::/7 — ULA (RFC 4193): a LAN do usuário/datacenter.
    if ((g0 & 0xfe00) === 0xfc00)
        return true;
    // fe80::/10 — link-local.
    if ((g0 & 0xffc0) === 0xfe80)
        return true;
    // fec0::/10 — site-local. Deprecado (RFC 3879), mas é LAN por definição e
    // stacks antigas ainda roteiam: nenhum CDN legítimo mora aí.
    if ((g0 & 0xffc0) === 0xfec0)
        return true;
    // ff00::/8 — multicast. Não é "privado", mas um fetch de servidor pra multicast
    // só serve pra varrer a rede (`ff02::1` = all-nodes do link).
    if ((g0 & 0xff00) === 0xff00)
        return true;
    return false;
}
/**
 * Detecta IP privado/loopback/link-local em IPv4 e IPv6. No servidor, mitiga
 * SSRF mesmo após redirect (DNS rebind); no cliente, barra a capa que aponta
 * pra LAN do usuário.
 *
 * Aceita a forma COM colchetes (`[::1]`) — que é a que `URL.hostname` produz e a
 * única que o caller do servidor realmente tem em mãos.
 *
 * Contrato de saída, nos três casos:
 *  - dotted-quad IPv4 → classificação por faixa (inalterada);
 *  - qualquer coisa com `:` → tratada como IPv6; se NÃO parsear, `true`
 *    (FAIL-CLOSED: um literal que nem a nossa engine entende não vira destino);
 *  - o resto (nome de host) → `false`; quem resolve nome é o guard de DNS.
 */
export function isPrivateIp(ip) {
    const value = canonicalizeIpLiteral(ip);
    // IPv4
    const v4 = value.match(/^(\d+)\.(\d+)\.(\d+)\.(\d+)$/);
    if (v4) {
        const a = Number(v4[1]);
        const b = Number(v4[2]);
        if (a === 10)
            return true; // 10.0.0.0/8 (RFC1918)
        if (a === 172 && b >= 16 && b <= 31)
            return true; // 172.16.0.0/12 (RFC1918)
        if (a === 192 && b === 168)
            return true; // 192.168.0.0/16 (RFC1918)
        if (a === 127)
            return true; // 127.0.0.0/8 (loopback)
        if (a === 169 && b === 254)
            return true; // 169.254.0.0/16 (link-local)
        if (a === 100 && b >= 64 && b <= 127)
            return true; // 100.64.0.0/10 (CGNAT)
        if (a === 0)
            return true; // 0.0.0.0/8
        return false;
    }
    // IPv6 (ou algo que se parece com um): classifica por GRUPOS, nunca por texto.
    if (value.includes(':')) {
        const groups = parseIpv6Groups(value);
        if (!groups)
            return true; // fail-closed
        return isPrivateIpv6Groups(groups);
    }
    return false;
}
/** TLDs/sufixos que, por definição, só resolvem DENTRO de uma rede local.
 *  `.local` é mDNS (RFC 6762), `.localhost` é reservado pro loopback (RFC 6761),
 *  `.home.arpa` é o nome de rede doméstica (RFC 8375), `.internal` é o padrão de
 *  facto de rede corporativa/cloud. Nenhuma capa legítima de painel público mora
 *  em nenhum deles. */
const LAN_SUFFIXES = ['.local', '.localhost', '.home.arpa', '.internal'];
/**
 * O hostname aponta pra rede local? PURO: decide só pelo texto do host, sem DNS.
 *
 * Cobre três formas:
 *  1. IP literal privado/loopback/link-local (v4 e v6, com ou sem colchetes).
 *  2. `localhost` e qualquer sufixo de LAN (`.local`, `.internal`, ...).
 *  3. Hostname de rótulo ÚNICO, sem ponto (`router`, `nas`, `openelec`). Não
 *     existe no DNS público — só resolve por mDNS/NetBIOS/search-domain, ou
 *     seja, LAN. Bloquear é seguro: uma capa nesse host já não carregaria pra
 *     ninguém fora da rede de quem a cadastrou.
 *
 * O que ele NÃO pega, de propósito: `evil.com` que RESOLVE pra 192.168.0.1. Isso
 * exige DNS e é trabalho do guard do servidor (ver o cabeçalho deste arquivo).
 */
export function isPrivateHostname(hostname) {
    if (!hostname)
        return true;
    // `new URL('http://[::1]/x').hostname` devolve COM colchetes. A normalização
    // mora em `canonicalizeIpLiteral` (fonte única) — antes esta linha era a ÚNICA
    // cópia que tirava os colchetes, e o guard do servidor, que não tinha a cópia,
    // deixava todo IPv6 literal passar.
    const h = canonicalizeIpLiteral(hostname);
    if (h === 'localhost')
        return true;
    if (LAN_SUFFIXES.some((suffix) => h.endsWith(suffix)))
        return true;
    if (isPrivateIp(h))
        return true;
    // Rótulo único (sem ponto) e não é IPv4 numérico já tratado acima.
    if (!h.includes('.') && !h.includes(':'))
        return true;
    return false;
}
/**
 * A URL é segura pra o CLIENTE buscar direto (sem passar pelo nosso proxy)?
 *
 * `true` = scheme http(s) **e** host que não é a rede local do usuário. É o
 * mínimo que o `<img>` da TV precisa checar antes de o `src` ser setado, já que
 * ali não há servidor nenhum no caminho pra checar por ele.
 *
 * NÃO checa porta — ver o comentário de `ALLOWED_PORTS`: capa de painel em porta
 * exótica é rotina, e sumir com ela seria trocar um bug de segurança por um bug
 * de produto pior.
 *
 * URL não-parseável → `false` (conservador). Relativa (`/x.png`, `data:`) também
 * → `false`: esta função é sobre origem EXTERNA, e o caller já trata o caso
 * local antes de chegar aqui.
 */
export function isPubliclyRoutableUrl(raw) {
    if (!raw)
        return false;
    let parsed;
    try {
        parsed = new URL(raw);
    }
    catch {
        return false;
    }
    if (!ALLOWED_SCHEMES.has(parsed.protocol))
        return false;
    return !isPrivateHostname(parsed.hostname);
}
//# sourceMappingURL=url-safety.js.map